import { KeySet } from '@tableau/data-structures';
import {
  ButtonType,
  ButtonWidget,
} from '@tableau/widgets';
import React from 'react';
import { v4 } from 'uuid';
import { Messages } from '../../../../../../gen-src/Localize';
import { ApiRequestsTypes } from '../../apiRequests/ApiRequests';
import { ApiRequestsContext } from '../../apiRequests/ApiRequestsContext';
import { useTelemetrySender } from '../../hooks/useTelemetrySender';
import { ComposeEmailDialogContainer, DialogResultParams } from './ComposeEmailDialogContainer';
import { ComposeEmailDialogStyles } from './ComposeEmailDialogStyles';
import { ImpactAnalysisEmailTelemetryEvent } from './ImpactAnalysisEmailTelemetryEvent';

export type ComposeEmailPaneButtonProps = {
  readonly selectedUserIds: KeySet;
  readonly contentType: ApiRequestsTypes.SendImpactAnalysisEmailContentType;
  readonly contentId: string;
  readonly contentLuid: string;
};

export function ComposeEmailPaneButton({ contentId, contentType, selectedUserIds, contentLuid }: ComposeEmailPaneButtonProps): JSX.Element {
  const [dialogSessionId] = React.useState(v4());
  const apiRequests = React.useContext(ApiRequestsContext);
  const [hasPermission, setHasPermission] = React.useState(false);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const sendDataInImpactAnalysisEmail = useTelemetrySender<ImpactAnalysisEmailTelemetryEvent>({
    type: 'impact_analysis_email_sent',
    content_luid: contentLuid,
    content_type: contentType,
    dialog_session_id: dialogSessionId,
    user_count: 0,
  });

  React.useEffect(() => {
    apiRequests.canUserSendImpactAnalysisEmail({ contentId, contentType })
      .then(r => setHasPermission(r))
      .catch(_ => { return; });
  }, [contentId, contentType, apiRequests]);

  const launchDialog = React.useCallback(() => {
    setIsDialogOpen(true);
    sendDataInImpactAnalysisEmail();
  }, [sendDataInImpactAnalysisEmail])

  const closeDialog = () => setIsDialogOpen(false);

  async function sendEmailAndCloseDialog(a: DialogResultParams) {
    try {
      await apiRequests.sendImpactAnalysisEmailAndShowToast({
        contentType,
        contentId,
        subject: a.subjectText,
        body: a.bodyText,
        userIds: Object.keys(selectedUserIds)
      });

      // TODO: remove toasting from vizportalclient and do that here
      closeDialog();
    } catch {
      // TODO: remove toasting from vizportalclient and do that here
      closeDialog();
    }
  }

  const hasSelection = Object.keys(selectedUserIds).length >= 1;
  const buttonEnabled = hasPermission && hasSelection;
  const tooltipText = !hasPermission
    ? Messages.SendEmailButtonNoPermissions()
    : !hasSelection ? Messages.SendEmailButtonNoSelection() : undefined;

  return (
    <>
      <ButtonWidget
        handleClick={launchDialog}
        buttonType={ButtonType.Outline}
        style={ComposeEmailDialogStyles.emailButtonStyle}
        disabled={!(buttonEnabled)}
        testId={ComposeEmailDialogStyles.TEST_ID('compose-button')}
        title={tooltipText}
      >
        {Messages.SendEmail()}
      </ButtonWidget>
      {
        isDialogOpen && (
          <ComposeEmailDialogContainer
            selectedUserIds={selectedUserIds}
            dismissDialog={closeDialog}
            submitResult={sendEmailAndCloseDialog}
            contentType={contentType}
            sendDataInImpactAnalysisEmail={sendDataInImpactAnalysisEmail}
            dialogSessionId={dialogSessionId}
            contentLuid={contentLuid}
          />
        )
      }
    </>
  );
}
